/**
 * Defines the type of the object
 */
var GraphQLType;
(function (GraphQLType) {
    GraphQLType[GraphQLType["SCALAR"] = 0] = "SCALAR";
    GraphQLType[GraphQLType["INLINE_FRAGMENT"] = 1] = "INLINE_FRAGMENT";
    GraphQLType[GraphQLType["FRAGMENT"] = 2] = "FRAGMENT";
})(GraphQLType || (GraphQLType = {}));
/**
 * The symbol to use to store the object GQL type
 */
var typeSymbol = Symbol('GraphQL Type');
/**
 * The symbol to use to store the object parameters.
 */
var paramsSymbol = Symbol('GraphQL Params');
/**
 * Checks whether an object is a fragment object.
 */
function isInlineFragmentObject(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value[typeSymbol] === GraphQLType.INLINE_FRAGMENT);
}
/**
 * Checks whether an object is a fragment object.
 */
function isFragmentObject(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value[typeSymbol] === GraphQLType.FRAGMENT);
}
/**
 * Checks whether an object is a scalar object.
 */
function isScalarObject(value) {
    return (typeof value === 'object' && value !== null && value[typeSymbol] === GraphQLType.SCALAR);
}
/**
 * Renders the name if it was given.
 */
function renderName(name) {
    return name === undefined ? '' : name;
}
/**
 * Renders the parameters if they were given.
 * @param params The query parameters
 * @param brackets Whether to surround the rendered parameter with parentheses (default: true)
 * @param array Whether to exclude the array index from the rendered parameter (default: false)
 * @returns The rendered parameter(s)
 */
function renderParams(params, brackets, array) {
    if (brackets === void 0) { brackets = true; }
    if (array === void 0) { array = false; }
    if (!params) {
        // If no params are given, don't render anything.
        return '';
    }
    var builder = [];
    for (var _i = 0, _a = Object.entries(params); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], value = _b[1];
        var params_1 = void 0;
        if (value === null) {
            params_1 = "null";
        }
        else if (Array.isArray(value)) {
            params_1 = "[" + renderParams(value, false, true) + "]";
        }
        else if (typeof value === 'object') {
            params_1 = "{" + renderParams(value, false) + "}";
        }
        else {
            params_1 = "" + value;
        }
        builder.push(array ? "" + params_1 : key + ":" + params_1);
    }
    var built = builder.join(',');
    if (brackets) {
        built = "(" + built + ")";
    }
    return built;
}
/**
 * Renders a GQL scalar.
 */
function renderScalar(name, params) {
    // Scalars have no content, just a name.
    return renderName(name) + renderParams(params);
}
/**
 * Renders a GQL inline fragment.
 */
function renderInlineFragment(fragment, context) {
    return "...on " + fragment.typeName + renderObject(undefined, fragment.internal, context);
}
/**
 * Renders a GQL fragment.
 */
function renderFragment(fragment, context) {
    return "fragment " + fragment.name + " on " + fragment.typeName + renderObject(undefined, fragment.internal, context);
}
/**
 * Renders a GQL array.
 */
function renderArray(name, arr, context) {
    // Get first item.
    var first = arr[0];
    // Ensure we have something.
    if (first === undefined || first === null) {
        throw new Error('Cannot render array with no first value');
    }
    first[paramsSymbol] = arr[paramsSymbol];
    // Render type normally.
    return renderType(name, first, context);
}
/**
 * Renders the given value into its given GQL.
 */
function renderType(name, value, context) {
    switch (typeof value) {
        case 'bigint':
        case 'boolean':
        case 'number':
        case 'string':
            throw new Error("Rendering type " + typeof value + " directly is disallowed");
        case 'object':
            // Ignore null fields.
            if (value === null) {
                throw new Error('Cannot render null');
            }
            if (isScalarObject(value)) {
                return renderScalar(name, value[paramsSymbol]) + " ";
            }
            else if (Array.isArray(value)) {
                return renderArray(name, value, context);
            }
            else {
                return renderObject(name, value, context);
            }
        case 'undefined':
            // Ignore undefined values.
            return '';
        default:
            throw new Error("Cannot render type " + typeof value);
    }
}
/**
 * Renders a object to GQL.
 */
function renderObject(name, obj, context) {
    var fields = [];
    // Iterate normal properties and render them accordingly.
    for (var _i = 0, _a = Object.entries(obj); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], value = _b[1];
        fields.push(renderType(key, value, context));
    }
    // Search for fragment & inline fragment symbols and render them.
    for (var _c = 0, _d = Object.getOwnPropertySymbols(obj); _c < _d.length; _c++) {
        var sym = _d[_c];
        var value = obj[sym];
        if (isInlineFragmentObject(value)) {
            fields.push(renderInlineFragment(value, context));
        }
        else if (isFragmentObject(value)) {
            context.fragments.set(sym, value);
            fields.push("..." + value.name);
        }
    }
    // Validate that we have fields to render.
    if (fields.length === 0) {
        throw new Error('Object cannot have no fields');
    }
    // Render function object.
    return "" + renderName(name) + renderParams(obj[paramsSymbol]) + "{" + fields.join('').trim() + "}";
}
/**
 * Performs a complete render of a given object.
 */
function render(value) {
    // Construct main render context.
    var context = {
        fragments: new Map(),
    };
    // Render main body.
    var rend = renderObject(undefined, value, context);
    // Render the fragments defined in each context until we have no more to render.
    var rendered = new Map();
    var executingContext = context; // The context we're currently executing over.
    var currentContext = {
        // The current context for execution.
        fragments: new Map(),
    };
    while (executingContext.fragments.size > 0) {
        // Use Array.from due to ES5 target without downLevelIteration enabled.
        for (var _i = 0, _a = Array.from(executingContext.fragments.entries()); _i < _a.length; _i++) {
            var _b = _a[_i], sym = _b[0], fragment = _b[1];
            // We only need to render the fragment once, even if it's used multiple times.
            if (!rendered.has(sym)) {
                rendered.set(sym, renderFragment(fragment, currentContext));
            }
        }
        // Set the next context to execute to the one we just used.
        executingContext = currentContext;
        currentContext = {
            // Reset current context.
            fragments: new Map(),
        };
    }
    return rend + Array.from(rendered.values()).join('');
}
function fragmentToString(value) {
    // Construct main render context.
    var context = {
        fragments: new Map(),
    };
    // Render main body.
    renderObject(undefined, value, context);
    // Render the fragments defined in each context until we have no more to render.
    var currentContext = {
        // The current context for execution.
        fragments: new Map(),
    };
    var output = '';
    for (var _i = 0, _a = Array.from(context.fragments.entries()); _i < _a.length; _i++) {
        var _b = _a[_i], fragment = _b[1];
        output = output + renderFragment(fragment, currentContext);
    }
    return output;
}

function createOperate(operateType) {
    function operate(opNameOrQueryObject, queryObject) {
        if (typeof opNameOrQueryObject === 'string') {
            if (!queryObject) {
                throw new Error('queryObject is not set');
            }
            return {
                toString: function () { return operateType + " " + opNameOrQueryObject + render(queryObject); },
            };
        }
        return {
            toString: function () { return "" + operateType + render(opNameOrQueryObject); },
        };
    }
    return operate;
}
/**
 * Create a typed-graphqlify instance which contains GraphQL query.
 */
var query = createOperate('query');
/**
 * Create a typed-graphqlify instance which contains GraphQL mutation.
 */
var mutation = createOperate('mutation');
/**
 * Create a typed-graphqlify instance which contains GraphQL subscription.
 */
var subscription = createOperate('subscription');
/**
 * You can also use `params` helper which is useful for inline arguments.
 *
 * ```typescript
 * import { mutation, params, rawString } from 'typed-graphqlify'
 *
 * mutation('updateUserMutation', {
 *   updateUser: params(
 *     {
 *       input: {
 *         name: rawString('Ben'),
 *         slug: rawString('/ben'),
 *       },
 *     },
 *     {
 *       id: types.number,
 *       name: types.string,
 *     },
 *   ),
 * })
 * ```
 */
function params(params, input) {
    if (typeof params !== 'object') {
        throw new Error('Params have to be an object');
    }
    if (typeof input !== 'object') {
        throw new Error("Cannot apply params to JS " + typeof params);
    }
    input[paramsSymbol] = params;
    return input;
}
/**
 * Use `alias` to write GraphQL alias. It should be placed as a dynamic property.
 *
 * ```graphql
 * query getMaleUser {
 *   maleUser: user {
 *     id
 *     name
 *   }
 * }
 * ```
 * import { alias, query, types } from 'typed-graphqlify'
 *
 * query('getMaleUser', {
 *   [alias('maleUser', 'user')]: {
 *     id: types.number,
 *     name: types.string,
 *   },
 * }
 */
function alias(alias, target) {
    return alias + ":" + target;
}
/**
 * Use the `fragment` helper to create GraphQL Fragment, and spread the result into places the fragment is used.
 *
 * ```typescript
 * import { alias, fragment, query } from 'typed-graphqlify'
 *
 * const bankAccountFragment = fragment('bankAccountFragment', 'BankAccount', {
 *   id: types.number,
 *   branch: types.string,
 * })
 *
 * const userFragment = fragment('userFragment', 'User', {
 *   id: types.number,
 *   name: types.string,
 *   bankAccount: {
 *     ...bankAccountFragment,
 *   },
 * })
 *
 * query({
 *   [alias('user', 'user(id: 1)')], {
 *     ...userFragment,
 *   },
 *   [alias('maleUsers', 'users(sex: MALE)')], {
 *     ...userFragment,
 *   },
 * }
 * ```
 */
function fragment(name, typeName, input) {
    var _a, _b;
    var fragment = (_a = {},
        _a[typeSymbol] = GraphQLType.FRAGMENT,
        _a.name = name,
        _a.typeName = typeName,
        _a.internal = input,
        _a);
    return _b = {}, _b[Symbol("Fragment(" + name + " on " + typeName + ")")] = fragment, _b;
}
/**
 * prevent `String` param being rendered as enum.
 */
function rawString(input) {
    return JSON.stringify(input);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

/**
 * Add `types.optional` or `optional` helper method to define optional field.
 *
 * ```typescript
 * import { optional, query, types } from 'typed-graphqlify'
 *
 * query('getUser', {
 *   user: {
 *     id: types.number,
 *     name: types.optional.string, // <-- user.name is `string | undefined`
 *     bankAccount: optional({      // <-- user.bankAccount is `{ id: number } | undefined`
 *       id: types.number,
 *     }),
 *   },
 * }
 * ```
 */
function optional(obj) {
    return obj;
}
function on(typeName, internal) {
    var _a, _b;
    var fragment = (_a = {},
        _a[typeSymbol] = GraphQLType.INLINE_FRAGMENT,
        _a.typeName = typeName,
        _a.internal = internal,
        _a);
    return _b = {}, _b[Symbol("InlineFragment(" + typeName + ")")] = fragment, _b;
}
function onUnion(types) {
    var fragments = {};
    for (var _i = 0, _a = Object.entries(types); _i < _a.length; _i++) {
        var _b = _a[_i], typeName = _b[0], internal = _b[1];
        fragments = __assign(__assign({}, fragments), on(typeName, internal));
    }
    return fragments;
}
function scalarType() {
    var _a;
    var scalar = (_a = {},
        _a[typeSymbol] = GraphQLType.SCALAR,
        _a);
    return scalar;
}
var types = /** @class */ (function () {
    function types() {
    }
    Object.defineProperty(types, "number", {
        get: function () {
            return scalarType();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(types, "string", {
        get: function () {
            return scalarType();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(types, "boolean", {
        get: function () {
            return scalarType();
        },
        enumerable: false,
        configurable: true
    });
    types.constant = function (_c) {
        return scalarType();
    };
    types.oneOf = function (_e) {
        return scalarType();
    };
    types.custom = function () {
        return scalarType();
    };
    types.optional = types;
    return types;
}());

export { alias, fragment, fragmentToString, mutation, on, onUnion, optional, params, query, rawString, subscription, types };
//# sourceMappingURL=index.es.js.map
